// Web Bluetooth (GATT) printing for the MT580P thermal receipt printer.
// Ported from the working implementation in sweety-cafe-billing-system-main
// (src/utils/bluetooth.js + src/hooks/usePrinter.js), which prints to this
// exact same physical printer successfully. USB was a dead end on this
// machine (kernel's usblp driver claims the device, no CUPS driver exists
// for it either) - this printer's BLE GATT interface sidesteps both problems
// entirely, no OS-level setup needed.

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

const PRINTER_SERVICE_UUIDS = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
];
const PRINTER_CHAR_UUIDS = [
  "00002af1-0000-1000-8000-00805f9b34fb",
  "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f",
];

// Cached at module scope (not React state) so the BLE connection survives
// the print modal being closed/reopened - avoids re-prompting the device
// picker on every single print.
let cachedCharacteristic = null;
let cachedDevice = null;

const cmd = (...bytes) => new Uint8Array(bytes);

const textBytes = (str) => new TextEncoder().encode(String(str).replace(/₹/g, "Rs."));

const line = (text) => [...textBytes(text), LF];

const leftRight = (left, right, width = 32) => {
  left = String(left);
  right = String(right);
  const gap = width - left.length - right.length;
  if (gap <= 0) return left + " " + right;
  return left + " ".repeat(gap) + right;
};

const separator = (width = 32) => "-".repeat(width);

const buildEscPos = (bill) => {
  const other = bill.otherDetails || {};
  const fixedCharges = bill.fixedCharges || 0;

  const bytes = [];
  const push = (...parts) => {
    for (const p of parts) {
      if (p instanceof Uint8Array || Array.isArray(p)) bytes.push(...p);
      else bytes.push(p);
    }
  };

  push(...cmd(ESC, 0x40)); // init
  push(...cmd(ESC, 0x61, 0x01)); // center

  push(...cmd(ESC, 0x45, 0x01)); // bold on
  push(...line("Govt. of Madhya Pradesh"));
  push(...line(`Nagar Parishad ${other.schemeName || "NA"}`));
  push(...cmd(ESC, 0x45, 0x00)); // bold off
  push(...line(`${bill.schemedistrict || "NA"} District (M.P.)`));
  push(LF);

  push(...cmd(ESC, 0x61, 0x00)); // left align
  push(...line(separator()));
  push(...cmd(ESC, 0x45, 0x01));
  push(...line("Customer Details"));
  push(...cmd(ESC, 0x45, 0x00));
  push(...line(separator()));
  push(...line(leftRight("Cust Name", other.consumerName || "NA")));
  push(...line(leftRight("Cust ID", bill.consumerID || "NA")));
  push(...line(leftRight("Mobile No", other.mobileNumber || "NA")));
  push(...line(leftRight("Ward No", bill.wardNumber || "NA")));
  push(LF);

  push(...cmd(ESC, 0x61, 0x01)); // center
  push(...cmd(ESC, 0x45, 0x01));
  push(...line("Bill Details"));
  push(...cmd(ESC, 0x45, 0x00));
  push(...cmd(ESC, 0x61, 0x00)); // left align
  push(...line(separator()));
  push(...line(leftRight("Town", other.schemeName || "NA")));
  push(...line(leftRight("Bill No", bill.billID || "NA")));
  push(...line(leftRight("Bill Month", bill.month || "NA")));
  push(...line(leftRight("Bill Year", bill.year || "NA")));
  push(...line(leftRight("Fixed Charges", `Rs.${fixedCharges}.00`)));
  push(...line(separator()));

  push(...cmd(ESC, 0x45, 0x01)); // bold
  push(...line(leftRight("TOTAL", `Rs.${fixedCharges}.00`)));
  push(...cmd(ESC, 0x45, 0x00));
  push(...line(separator()));

  push(...cmd(ESC, 0x61, 0x01)); // center
  push(...cmd(ESC, 0x45, 0x01));
  push(...line("Rean WaterTech Pvt. Ltd."));
  push(...cmd(ESC, 0x45, 0x00));
  push(LF, LF, LF);

  push(...cmd(GS, 0x56, 0x00)); // cut

  return new Uint8Array(bytes);
};

const getCharacteristic = async () => {
  if (cachedCharacteristic && cachedDevice?.gatt?.connected) {
    return cachedCharacteristic;
  }

  if (!navigator.bluetooth) {
    throw new Error("This browser doesn't support Web Bluetooth (use Chrome or Edge).");
  }

  const device = await navigator.bluetooth
    .requestDevice({
      filters: [{ services: [PRINTER_SERVICE_UUIDS[0]] }],
      optionalServices: PRINTER_SERVICE_UUIDS,
    })
    .catch(() =>
      navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: PRINTER_SERVICE_UUIDS,
      })
    );

  device.addEventListener("gattserverdisconnected", () => {
    cachedCharacteristic = null;
    cachedDevice = null;
  });

  const server = await device.gatt.connect();

  let characteristic = null;
  for (const svcUuid of PRINTER_SERVICE_UUIDS) {
    try {
      const service = await server.getPrimaryService(svcUuid);
      for (const charUuid of PRINTER_CHAR_UUIDS) {
        try {
          characteristic = await service.getCharacteristic(charUuid);
          break;
        } catch {
          // this characteristic UUID isn't on this service - try the next one
        }
      }
      if (characteristic) break;
    } catch {
      // this service UUID isn't on this device - try the next one
    }
  }

  if (!characteristic) {
    throw new Error("Could not find a writable printer characteristic on this device.");
  }

  cachedDevice = device;
  cachedCharacteristic = characteristic;
  return characteristic;
};

export const printBillViaBluetooth = async (bill) => {
  const characteristic = await getCharacteristic();
  const bytes = buildEscPos(bill);

  const chunkSize = 512;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    await characteristic.writeValue(bytes.slice(i, i + chunkSize));
  }
};
